import type React from 'react';
import clsx from 'clsx';

import { ButtonTw } from './button-tw';

/**
 * The root container for pagination navigation.
 */
export function Pagination({
  'aria-label': ariaLabel = 'Page navigation',
  className,
  ...props
}: React.ComponentPropsWithoutRef<'nav'>) {
  return (
    <nav
      aria-label={ariaLabel}
      {...props}
      className={clsx(className, 'flex gap-x-2')}
    />
  );
}

/**
 * A link to the previous page in the pagination list.
 */
export function PaginationPrevious({
  href = null,
  className,
  children = 'Previous',
}: React.PropsWithChildren<{ 
  /** The URL of the previous page. */
  href?: string | null; 
  /** Optional CSS class name. */
  className?: string 
}>) {
  return (
    <span className={clsx(className, 'grow basis-0')}>
      <ButtonTw
        {...(href === null ? { disabled: true } : { href })}
        aria-label="Previous page"
        plain
      >
        <svg
          aria-hidden="true"
          className="stroke-current"
          data-slot="icon"
          fill="none"
          viewBox="0 0 16 16"
        >
          <path
            d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
          />
        </svg>
        {children}
      </ButtonTw>
    </span>
  );
}

/**
 * A link to the next page in the pagination list.
 */
export function PaginationNext({
  href = null,
  className,
  children = 'Next',
}: React.PropsWithChildren<{ 
  /** The URL of the next page. */
  href?: string | null; 
  /** Optional CSS class name. */
  className?: string 
}>) {
  return (
    <span className={clsx(className, 'flex grow basis-0 justify-end')}>
      <ButtonTw
        {...(href === null ? { disabled: true } : { href })}
        aria-label="Next page"
        plain
      >
        {children}
        <svg
          aria-hidden="true"
          className="stroke-current"
          data-slot="icon"
          fill="none"
          viewBox="0 0 16 16"
        >
          <path
            d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
          />
        </svg>
      </ButtonTw>
    </span>
  );
}

/**
 * A container for the individual page links.
 */
export function PaginationList({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(className, 'hidden items-baseline gap-x-2 sm:flex')}
    />
  );
}

/**
 * An individual page link within the pagination list.
 */
export function PaginationPage({
  href,
  className,
  current = false,
  children,
}: React.PropsWithChildren<{
  /** The URL of the page. */
  href: string;
  /** Optional CSS class name. */
  className?: string;
  /** Whether this page is the current active page. */
  current?: boolean;
}>) {
  return (
    <ButtonTw
      aria-current={current ? 'page' : undefined}
      aria-label={`Page ${children}`}
      className={clsx(
        className,
        'min-w-[2.25rem] before:absolute before:-inset-px before:rounded-lg',
        current && 'before:bg-gray-950/5 dark:before:bg-white/10'
      )}
      href={href}
      plain
    >
      <span className="-mx-0.5">{children}</span>
    </ButtonTw>
  );
}

/**
 * Represents a gap in the pagination list (usually an ellipsis).
 */
export function PaginationGap({
  className,
  children = <>&hellip;</>,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      aria-hidden="true"
      {...props}
      className={clsx(
        className,
        'w-[2.25rem] select-none text-center text-sm/6 font-semibold text-gray-950 dark:text-white'
      )}
    >
      {children}
    </span>
  );
}
